import {ColorSchemeType, Flag, Separator} from 'platform/components';
import {Box, Center, Heading, HStack, Icon, Link, Show, Text, VStack} from 'platform/foundation';
import {formatPhoneNumber, useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {ReactNode} from 'react';

import {isEmpty, isNotNil, uniq} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {TestDriveState} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

interface TestDriveTooltipCardProps extends RequiredTestIdProps {
  vehicleTitle: string | Nullish;
  state: TestDriveState;
  start: Date;
  end: Date | Nullish;
  customerName: string | Nullish;
  phoneNumbers: string[];
  isCompanyCustomer?: boolean;
  tags?: ReactNode;
  employeeName?: string;
  businessCaseCode?: string | Nullish;
  onBusinessCaseClick?: () => void;
  note?: string | Nullish;
}

const ICON_BOX_SIZE = 10;

export function TestDriveTooltipCard(props: TestDriveTooltipCardProps) {
  const formatDateTime = useDateTimeFormatter();

  const phoneNumbers = uniq(props.phoneNumbers);

  const stateFlag = match<TestDriveState, {colorScheme: ColorSchemeType; label: string}>(
    props.state
  )
    .with('planned', () => ({
      colorScheme: 'yellow',
      label: i18n.t('entity.testDrive.labels.planned'),
    }))
    .with('completed', () => ({
      colorScheme: 'green',
      label: i18n.t('entity.testDrive.labels.completed'),
    }))
    .with('cancelled', () => ({
      colorScheme: 'red',
      label: i18n.t('entity.testDrive.labels.cancelled'),
    }))
    .exhaustive();

  const customerIcon = props.isCompanyCustomer ? 'communication/business' : 'social/person';

  const endDate = isNotNil(props.end) ? formatDateTime('dateMedium', props.end) : null;
  const endTime = isNotNil(props.end) ? formatDateTime('timeShort', props.end) : null;

  return (
    <Box
      backgroundColor="general.white"
      border="1px solid"
      borderColor="palettes.neutral.40.100"
      borderRadius="medium"
      padding={4}
      boxShadow="elevation_4"
      width={100}
      maxWidth={['calc(100vw - 1rem)', 100]}
      data-testid={suffixTestId('testDriveTooltip', props)}
    >
      <VStack spacing={4}>
        <HStack spacing={2}>
          <Heading size={4}>{props.vehicleTitle ?? EMPTY_PLACEHOLDER}</Heading>
          <Flag colorScheme={stateFlag.colorScheme} label={stateFlag.label} />
        </HStack>

        <HStack width="100%" spacing={4}>
          <Box flex={1}>
            <HStack spacing={2} align="center">
              <Box
                width={ICON_BOX_SIZE}
                height={ICON_BOX_SIZE}
                backgroundColor="palettes.neutral.30.100"
                borderRadius="small"
              >
                <Center height="100%" width="100%">
                  <Icon value="navigation/arrow_upward" size={6} />
                </Center>
              </Box>

              <VStack spacing={0}>
                <Heading size={5}>{formatDateTime('dateMedium', props.start)}</Heading>
                <Text size="small" color="secondary">
                  {formatDateTime('timeShort', props.start)}
                </Text>
              </VStack>
            </HStack>
          </Box>

          <Box flex={1}>
            <HStack spacing={2} align="center">
              <Box
                width={ICON_BOX_SIZE}
                height={ICON_BOX_SIZE}
                backgroundColor="palettes.neutral.30.100"
                borderRadius="small"
              >
                <Center height="100%" width="100%">
                  <Icon value="navigation/arrow_upward" size={6} />
                </Center>
              </Box>

              <VStack spacing={0}>
                <Heading size={5}>{endDate ?? EMPTY_PLACEHOLDER}</Heading>
                <Show when={isNotNil(endTime)}>
                  <Text size="small" color="secondary">
                    {endTime}
                  </Text>
                </Show>
              </VStack>
            </HStack>
          </Box>
        </HStack>

        <Separator spacing={0} />

        <VStack spacing={4}>
          <HStack spacing={1} align="center">
            <Icon value={customerIcon} size={4} />
            <Heading size={5}>{i18n.t('entity.customer.labels.customer')}</Heading>
            <Show when={isNotNilOrEmpty(props.tags)}>
              <HStack spacing={1}>{props.tags}</HStack>
            </Show>
          </HStack>

          <VStack spacing={1}>
            <HStack width="100%" justify="space-between">
              <Box flex={1}>
                <Text size="small" color="secondary">
                  {i18n.t('entity.person.labels.fullName')}
                </Text>
              </Box>
              <Box flex={1}>
                <Text size="small">{props.customerName ?? EMPTY_PLACEHOLDER}</Text>
              </Box>
            </HStack>

            <Separator spacing={2} />

            <HStack width="100%" justify="space-between" align="flex-start">
              <Box flex={1}>
                <Text size="small" color="secondary">
                  {i18n.t('entity.person.labels.phoneNumber')}
                </Text>
              </Box>
              <Box flex={1}>
                {isEmpty(phoneNumbers) ? (
                  <Text size="small">{EMPTY_PLACEHOLDER}</Text>
                ) : (
                  <VStack spacing={0}>
                    {phoneNumbers.map((phoneNumber) => (
                      <Text key={phoneNumber} size="small">
                        {formatPhoneNumber(phoneNumber) ?? phoneNumber}
                      </Text>
                    ))}
                  </VStack>
                )}
              </Box>
            </HStack>

            <Show when={isNotNilOrEmpty(props.employeeName)}>
              <Separator spacing={2} />

              <HStack width="100%" justify="space-between">
                <Box flex={1}>
                  <Text size="small" color="secondary">
                    {i18n.t('entity.businessCase.labels.testDriveEmployee')}
                  </Text>
                </Box>
                <Box flex={1}>
                  <Text size="small">{props.employeeName}</Text>
                </Box>
              </HStack>
            </Show>

            <Show when={isNotNilOrEmpty(props.businessCaseCode)}>
              <Separator spacing={2} />

              <HStack width="100%" justify="space-between">
                <Box flex={1}>
                  <Text size="small" color="secondary">
                    {i18n.t('page.businessCase.labels.businessCase')}
                  </Text>
                </Box>
                <Box flex={1}>
                  {isNotNil(props.onBusinessCaseClick) ? (
                    <Link
                      size="small"
                      title={props.businessCaseCode ?? undefined}
                      onClick={props.onBusinessCaseClick}
                      data-testid={suffixTestId('businessCaseLink', props)}
                    />
                  ) : (
                    <Text size="small">{props.businessCaseCode}</Text>
                  )}
                </Box>
              </HStack>
            </Show>

            <Show when={isNotNilOrEmpty(props.note)}>
              <Separator spacing={2} />

              <HStack width="100%" justify="space-between">
                <Box flex={1}>
                  <Text size="small" color="secondary">
                    {i18n.t('general.labels.note')}
                  </Text>
                </Box>
                <Box flex={1}>
                  <Text size="small">{props.note ?? EMPTY_PLACEHOLDER}</Text>
                </Box>
              </HStack>
            </Show>
          </VStack>
        </VStack>
      </VStack>
    </Box>
  );
}
